import {
  EProjectPermission,
  type IGlobalSearchItem,
} from "@frontend-shared/types";
import { getGlobalSearchItemTarget } from "@frontend-shared/utils";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { useNotificationsDrawer } from "@/components/notifications/hooks/use-notifications-drawer";
import { useActiveOrganizationUuid } from "@/hooks/use-active-organization-uuid";
import { useAuth } from "@/hooks/use-auth";
import { usePermissionCheck } from "@/hooks/use-permission";
import { resolveGlobalSearchDestination } from "@/lib/global-search/resolve-global-search-destination";
import { EGlobalSearchDestinationType } from "@/lib/global-search/types";

const CUSTOM_INTEGRATION_WRITE: EProjectPermission[] = [
  EProjectPermission.CUSTOM_INTEGRATION_WRITE,
];

interface IUseGlobalSearchSelectProps {
  onClose: () => void;
}

// NOTE: NSAI-6589 — a picked result: shared target → web destination → the
// navigation, project switch or drawer it stands for. The palette closes
// first, so a destination that re-renders the layout never races the dialog.
export const useGlobalSearchSelect = ({
  onClose,
}: IUseGlobalSearchSelectProps): ((item: IGlobalSearchItem) => void) => {
  const navigate = useNavigate();
  const { onCurrentProjectChange } = useAuth();
  const { openDrawer } = useNotificationsDrawer();
  const activeOrganizationUuid = useActiveOrganizationUuid();
  const [canEditCustomIntegrations] = usePermissionCheck(
    CUSTOM_INTEGRATION_WRITE,
  );

  return useCallback(
    (item: IGlobalSearchItem): void => {
      const target = getGlobalSearchItemTarget(item);
      if (!target) return;

      const destination = resolveGlobalSearchDestination({
        target,
        activeOrganizationUuid,
        canEditCustomIntegrations,
      });
      onClose();

      switch (destination.type) {
        case EGlobalSearchDestinationType.NAVIGATE:
          navigate(destination.path, { state: destination.state });
          return;
        case EGlobalSearchDestinationType.OPEN_WINDOW:
          globalThis.open(destination.path, "_blank", "noopener,noreferrer");
          return;
        case EGlobalSearchDestinationType.SWITCH_PROJECT:
          onCurrentProjectChange(destination.projectUuid);
          return;
        case EGlobalSearchDestinationType.OPEN_NOTIFICATIONS:
          openDrawer();
      }
    },
    [
      activeOrganizationUuid,
      canEditCustomIntegrations,
      navigate,
      onClose,
      onCurrentProjectChange,
      openDrawer,
    ],
  );
};
